import { createSingleDeliveryText } from "@/shared/plant/deliveryText";
// Preserve imports used by the existing calendars.
// eslint-disable-next-line react-refresh/only-export-components
export { createSingleDeliveryText } from "@/shared/plant/deliveryText";
import { isBauhofOrder } from "@/shared/orders/orderDeadlines";
import { Paper, Button, Box, IconButton, Alert, AlertTitle, Tooltip } from "@mui/material";
import { Fragment, useCallback, type ReactNode } from "react";
import { Add, Delete, Edit } from "@mui/icons-material";
import { initialMaterialOrder, MaterialOrder, SupplierOrder } from "../Project";
import EnhancedTable from "@/shared/views/EnhancedTable";
import { TopBar } from "@/shared/structure/TopBar";
import useRights from "@/shared/api/useRights";
import { xBFormatter } from "@/components/calendar/dialogs/OrderHistoryDialog";
import MaterialEditor from "../dialogs/newMaterialOrder/MaterialEditor";
import { split } from "lodash";

export const createArticleText = (
  row: MaterialOrder,
  shorten: boolean = true,
  enforceString: boolean = false,
  splitBy: "all" | "amount" | "article" = "all",
): JSX.Element | string => {
  if (!row.article) return splitBy === "amount" ? "" : "Kein Material";

  const buildData = () => {
    const entries: {
      amount: number;
      unit: string;
      name?: string;
      number?: string;
      roundsUnit?: boolean;
    }[] = [];

    entries.push({
      amount: row.amount,
      unit: row.article.unit,
      name: row.article?.name,
      number: row.article?.number,
      roundsUnit: row.roundsUnit,
    });

    for (let aa of row.additionalArticles) {
      if (aa.deleted) continue;

      entries.push({
        amount: aa.amount,
        unit: aa.article.unit,
        name: aa.article?.name,
        number: aa.article?.number,
      });
    }

    return entries;
  };

  const entries = buildData();

  const formatEntry = (e: (typeof entries)[number]) => {
    const formattedAmount = e.amount.toString().replace(/\./g, ",");
    const resolvedUnit = !e.roundsUnit ? e.unit : e.amount > 1 ? "Touren" : "Tour";

    return {
      strong: `${formattedAmount} ${resolvedUnit}`,
      rest: `${e.name} (${e.number})`,
      full: `${formattedAmount} ${resolvedUnit} ${e.name} (${e.number})`,
    };
  };

  const formatted = entries.map(formatEntry);

  // ✅ STRING MODE (no dash, no shorten, all entries)
  if (enforceString) {
    if (formatted.length > 1 && splitBy === "amount") {
      return "";
    } else if (formatted.length > 1 || splitBy === "all") {
      return formatted.map((f) => f.full).join("\n");
    } else if (splitBy === "article") {
      return formatted.map((f) => f.rest).join("\n");
    } else {
      return formatted.map((f) => f.strong).join("\n");
    }
  }

  // JSX MODE
  if (formatted.length === 1) {
    const f = formatted[0];
    return splitBy === "all" ? (
      <>
        <strong>{f.strong}</strong>&nbsp;{f.rest}
      </>
    ) : splitBy === "amount" ? (
      <strong>{f.strong}</strong>
    ) : (
      f.rest
    );
  }

  if (splitBy === "amount") return "";

  const maxcount = shorten ? 3 : 10;

  const content = (
    <div className="fffwrap">
      {formatted.map((f, i) => (
        <div key={i} className="fffarticle">
          - <strong>{f.strong}</strong>&nbsp;{f.rest}
        </div>
      ))}
    </div>
  );

  if (formatted.length <= maxcount) return content;

  return (
    <Tooltip title={content}>
      <div className="fffwrap">
        {formatted.slice(0, maxcount - 1).map((f, i) => (
          <div key={i} className="fffarticle">
            - <strong>{f.strong}</strong>&nbsp;{f.rest}
          </div>
        ))}
        <div className="fffarticle">
          <em>+ {formatted.length - (maxcount - 1)} mehr</em>
        </div>
      </div>
    </Tooltip>
  );
};


export function createDeliveryText(transportInformation: MaterialOrder["transportInformation"], takt: MaterialOrder["takt"], duration?: MaterialOrder["duration"], extended?: false): string;
export function createDeliveryText(transportInformation: MaterialOrder["transportInformation"], takt: MaterialOrder["takt"], duration: MaterialOrder["duration"] | undefined, extended: true): ReactNode[];
export function createDeliveryText(transportInformation: MaterialOrder["transportInformation"], takt: MaterialOrder["takt"], duration: MaterialOrder["duration"] | undefined, extended: boolean): string | ReactNode[];
export function createDeliveryText(
  transportInformation: MaterialOrder["transportInformation"],
  takt: MaterialOrder["takt"],
  duration: MaterialOrder["duration"] = "INVALID",
  extended: boolean = false,
): string | ReactNode[] {
  const extraTakt = (transportInformation ?? []).some((xx) => !!xx.specialTakt || xx.specialTakt === 0);

  const parts = (transportInformation ?? []).map((tt, i) =>
    extended ? (
      <div key={i} className="tiPart">
        {createSingleDeliveryText(tt, true, extraTakt ? takt : false, duration)}
      </div>
    ) : (
      createSingleDeliveryText(tt, false, extraTakt ? takt : false, duration)
    ),
  );

  if (parts.length === 0 && !extended) {
    return "-";
  }

  if (parts.length > 0 && !extraTakt && takt > 0) {
    parts.push(
      extended ? (
        <div key={"takt"} className="tiPart">
          {"Takt: " + takt + " min."}
        </div>
      ) : (
        "Takt: " + takt + " min."
      ),
    );
  }

  if (extended) return parts.length === 0 ? [" - "] : parts;
  return parts.join(", ");
}

const headCells = [
  {
    id: "_time",
    label: "Uhrzeit",
    numeric: true,
    sortable: true,
  },
  {
    id: "_lieferant",
    label: "Lieferant",
    numeric: false,
    sortable: true,
  },
  {
    id: "_artikel",
    label: "Artikel",
    numeric: false,
    sortable: true,
  },
  {
    id: "_delivery",
    label: "Logistik",
    numeric: false,
    sortable: true,
  },
  {
    id: "_comment",
    label: "Weitere Informationen",
    numeric: false,
    sortable: true,
  },
];

export default function MaterialTab({
  supplierOrders,
  changeable = true,
  bauhofOnly = false,
  bauhofDeadline,
  jobState = [],
  projectId,
  jobPosition,
  setJobState = (_e) => {},
}: {
  supplierOrders: SupplierOrder[];
  changeable: boolean;
  bauhofOnly?: boolean;
  bauhofDeadline?: string;
  projectId: number;
  jobState: MaterialOrder[];
  setJobState: (data: MaterialOrder[]) => void;
}) {
  const rights = useRights();
  const isDispo = "DISPOSITION" in rights;
  const deleteJobOrder = useCallback(
    (index: number) => {
      setJobState(
        jobState.slice().map((so, i) => ({
          ...so,
          changed: true,
          deleted: !!so.deleted || i === index,
        })),
      );
    },
    [jobState],
  );

  const saveJobOrder = useCallback(
    (subOrder: MaterialOrder, index: number) => {
      if (index === -1) {
        const newState = jobState.slice();
        newState.push(subOrder);
        setJobState(newState);
      } else {
        setJobState(jobState.slice().map((so, i) => (i === index ? { ...subOrder, changed: true } : so)));
      }
    },
    [jobState],
  );

  const hasJobOrders = jobState.filter((x) => !x.deleted).length > 0;

  const rowFormatter = useCallback(
    (row: MaterialOrder) => ({
      ...row,
      _time: !row.firstUnload
        ? "anschließend"
        : !row.firstUnload.startsWith("x")
          ? row.firstUnload
          : row.firstUnload.startsWith("xB")
            ? xBFormatter(row.firstUnload, "INFO", false)
            : row.firstUnload === "xAM"
              ? "vormittags"
              : row.firstUnload === "xPM"
                ? "nachmittags"
                : "???",
      _lieferant: !!row.article ? row.article?.plantName + " (" + row.article?.supplierName + ")" : "",
      _artikel: createArticleText(row),
      _delivery: createDeliveryText(row.transportInformation, row.takt),
      _comment: [
        !row.position && !!row.article && !row.article.bauhof ? "Unbepreister Artikel!" : "",
        row.easyTarget === "LAGER" ? "Anlieferung zum Lagerplatz!" : "",
        row.comment,
      ]
        .filter((x) => x.length > 0)
        .join(" - "),
    }),
    [],
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      <TopBar>
        {!changeable ? null : (
          <MaterialEditor
            bauhofOnly={bauhofOnly}
            bauhofDeadline={bauhofDeadline}
            jobId={0}
            manualPosition={jobPosition}
            projectId={projectId}
            saveType="CALLBACK"
            onSave={(o) => saveJobOrder(o, -1)}
            Opener={({ onClick }) => (
              <Button startIcon={<Add />} onClick={onClick}>
                Bestellung hinzufügen
              </Button>
            )}
            initialState={{ ...initialMaterialOrder() }}
          />
        )}
      </TopBar>
      <Box sx={{ flex: 1, overflow: "auto", background: "#f5f5f5", padding: 2 }}>
        {hasJobOrders ? (
          <Paper>
            <EnhancedTable
              initialRows={100}
              buttons={(row) =>
                !changeable || (bauhofOnly && !isBauhofOrder(row)) || (!isDispo && !!row.orderSentAt) ? null : (
                  <Fragment>
                    <MaterialEditor
                      bauhofOnly={bauhofOnly}
                      bauhofDeadline={bauhofDeadline}
                      jobId={0}
                      manualPosition={jobPosition}
                      projectId={projectId}
                      saveType="CALLBACK"
                      onSave={(order) => saveJobOrder(order, row.index)}
                      Opener={({ onClick }) => (
                        <IconButton size="small" onClick={onClick}>
                          <Edit />
                        </IconButton>
                      )}
                      initialState={jobState[row.index]}
                    />
                    <IconButton size="small" onClick={() => deleteJobOrder(row.index)}>
                      <Delete />
                    </IconButton>
                  </Fragment>
                )
              }
              type=""
              noEmptyCells
              initialSort="_time"
              headCells={headCells}
              rowFormatter={rowFormatter}
              rows={jobState.map((x, index) => ({ ...x, index })).filter((x) => !x.deleted)}
            />
          </Paper>
        ) : (
          <Alert severity="info" variant="filled">
            <AlertTitle>Nichts gefunden.</AlertTitle>
            Hier wurde noch nichts bestellt.
          </Alert>
        )}
      </Box>
    </Box>
  );
}
